import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usersService } from '../../services/db';
import { ROUTES } from '../../constants/routes';
import EditProfileModal from '../../components/user/EditProfileModal';
import Spinner from '../../components/ui/Spinner';
import {
  TrophyIcon,
  StreakFireIcon,
  GameControllerIcon,
  BibleIcon,
  MedalBadge,
} from '../../components/common/Icons';

/**
 * Helper to format seconds into "Xh Ym" or "Z mins"
 */
const formatTimeSpent = (totalSeconds) => {
  const secs = Number(totalSeconds) || 0;
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
};

/**
 * Helper to format date timestamps
 */
const formatDate = (val) => {
  if (!val) return null;
  try {
    const d = val.toDate ? val.toDate() : new Date(val);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
};

const UserProfilePage = () => {
  const { userId: paramUserId } = useParams();
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();

  // If paramUserId is present and matches logged in user or no paramUserId, it's own profile
  const targetUserId = paramUserId || authUser?.uid;
  const isOwnProfile = !paramUserId || (authUser?.uid && paramUserId === authUser.uid);

  const [profile, setProfile] = useState(null);
  const [recentGames, setRecentGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Fetch user data
  const loadProfile = useCallback(async () => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const userDoc = await usersService.getUserProfile(
        targetUserId,
        isOwnProfile ? authUser : null
      );
      setProfile(userDoc);

      // Fetch recent game activity
      const games = await usersService.getRecentGames(targetUserId, 5);
      setRecentGames(games);
    } catch (err) {
      console.error('Failed to load contestant profile:', err);
    } finally {
      setLoading(false);
    }
  }, [targetUserId, isOwnProfile, authUser]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Referral Copy Handlers
  const referralCode = profile?.referralCode || '';
  const referralLink = referralCode
    ? `go.biblecontest.app/r/${referralCode}`
    : '';

  const handleCopyCode = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.warn('Clipboard write failed:', err);
    }
  };

  const handleCopyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.warn('Clipboard write failed:', err);
    }
  };

  const handleShareLink = async () => {
    if (!referralLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on Bible Contest!',
          text: 'Test your Bible knowledge and win exciting prizes on Bible Contest!',
          url: referralLink,
        });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Share API failed, falling back to copy:', err);
        }
      }
    }
    handleCopyLink();
  };

  if (loading) {
    return (
      <div className="profile-page-loading">
        <Spinner size="lg" />
        <p style={{ marginTop: '16px', color: 'var(--clr-text-muted)', fontSize: '14px' }}>
          Loading profile & statistics...
        </p>
      </div>
    );
  }

  if (!profile && !targetUserId) {
    return (
      <div className="profile-page-container">
        <div className="profile-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👤</div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
            Please Sign In
          </h2>
          <p style={{ color: 'var(--clr-text-muted)', fontSize: '14px', marginBottom: '24px' }}>
            Sign in to view your devotion streaks, tournament ranking, and contest stats.
          </p>
          <button
            type="button"
            className="profile-primary-btn"
            onClick={() => navigate(ROUTES.AUTH.LOGIN)}
          >
            Sign In to Bible Contest
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page-container">
        <div className="profile-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
            Contestant Not Found
          </h2>
          <p style={{ color: 'var(--clr-text-muted)', fontSize: '14px', marginBottom: '24px' }}>
            We could not locate this player's profile in the Bible Contest directory.
          </p>
          <button
            type="button"
            className="profile-secondary-btn"
            onClick={() => navigate(ROUTES.GAMES.GLOBAL_LEADERBOARD)}
          >
            ← Back to Leaderboards
          </button>
        </div>
      </div>
    );
  }

  const joinDateFormatted = formatDate(profile.createdAt);
  const initials = (profile.userName || 'P')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="profile-page-container">
      {/* Top Breadcrumb Navigation */}
      <div className="profile-top-bar">
        {!isOwnProfile && (
          <button
            type="button"
            className="profile-back-btn"
            onClick={() => navigate(-1)}
          >
            <span>←</span>
            <span>Back to Rankings</span>
          </button>
        )}
      </div>

      {/* Main Profile Header Card */}
      <div className="profile-hero-card">
        <div className="profile-hero-main">
          {/* Avatar */}
          <div className="profile-hero-avatar-wrap">
            {profile.img ? (
              <img
                src={profile.img}
                alt={profile.userName}
                className="profile-hero-avatar-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="profile-hero-avatar-placeholder">{initials}</div>
            )}
            {profile.firstPlaceCount > 0 && (
              <div className="profile-champion-badge" title="Champion Contestant">
                <MedalBadge rank={1} size={22} />
              </div>
            )}
          </div>

          {/* User Details */}
          <div className="profile-hero-info">
            <div className="profile-hero-name-row">
              <h1 className="profile-hero-name">{profile.userName}</h1>
              
            </div>

            {/* Email (only on own profile) */}
            {isOwnProfile && profile.emailId && (
              <div className="profile-hero-email">
                <span>✉️</span>
                <span>{profile.emailId}</span>
              </div>
            )}

            {/* Demographics Chips */}
            <div className="profile-chips-row">
              {profile.country && (
                <div className="profile-chip" title="Country / Region">
                  <span className="profile-chip-icon">🌐</span>
                  <span>{profile.country}</span>
                </div>
              )}

              {profile.timezone && (
                <div className="profile-chip" title="Local Timezone">
                  <span className="profile-chip-icon">⏰</span>
                  <span>{profile.timezone.split('/').pop().replace(/_/g, ' ')}</span>
                </div>
              )}

              {/* Private Phone (Only visible on own profile) */}
              {isOwnProfile && profile.phone && (
                <div className="profile-chip profile-chip--private" title="Private Phone (Only visible to you)">
                  <span className="profile-chip-icon">📞</span>
                  <span>{profile.countryCode ? `${profile.countryCode} ` : ''}{profile.phone}</span>
                  <span className="profile-chip-lock" title="Private">🔒</span>
                </div>
              )}

              {joinDateFormatted && (
                <div className="profile-chip" title="Member Since">
                  <span className="profile-chip-icon">📅</span>
                  <span>Joined {joinDateFormatted}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hero Actions (Edit Profile on own profile) */}
        {isOwnProfile && (
          <div className="profile-hero-actions">
            <button
              type="button"
              className="profile-edit-btn"
              onClick={() => setEditModalOpen(true)}
            >
              <span>✏️</span>
              <span>Edit Profile</span>
            </button>
          </div>
        )}
      </div>

      {/* Unified Stats Banner (Quizzes, Points, 1st Places) */}
      <div className="profile-stats-card">
        <div className="profile-stat-col">
          <div className="profile-stat-icon-wrap" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}>
            <GameControllerIcon size={22} />
          </div>
          <div className="profile-stat-num">{profile.totalQuizzesPlayed}</div>
          <div className="profile-stat-label">Quizzes Played</div>
        </div>

        <div className="profile-stat-divider" />

        <div className="profile-stat-col">
          <div className="profile-stat-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}>
            <TrophyIcon size={22} />
          </div>
          <div className="profile-stat-num">{profile.totalPointsScored}</div>
          <div className="profile-stat-label">Total Points</div>
        </div>

        <div className="profile-stat-divider" />

        <div className="profile-stat-col">
          <div className="profile-stat-icon-wrap" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' }}>
            <MedalBadge rank={1} size={22} />
          </div>
          <div className="profile-stat-num">{profile.firstPlaceCount}</div>
          <div className="profile-stat-label">1st Places Won</div>
        </div>
      </div>

      {/* Performance & Devotion Streaks Grid */}
      <div className="profile-grid">
        {/* Card 1: Devotion Streak */}
        <div className="profile-metric-card">
          <div className="profile-metric-header">
            <div className="profile-metric-icon profile-metric-icon--flame">
              <StreakFireIcon size={22} style={{ color: '#FF5C56' }} />
            </div>
            <div className="profile-metric-badge">Daily Habit</div>
          </div>
          <div className="profile-metric-val">
            <span>{profile.currentStreak}</span>
            <span className="profile-metric-unit">days</span>
          </div>
          <div className="profile-metric-title">Daily Devotion Streak</div>
          <div className="profile-metric-sub">
            Personal Best: <strong>{profile.highestStreak} consecutive days</strong>
          </div>
        </div>

        {/* Card 2: Time in the Word */}
        <div className="profile-metric-card">
          <div className="profile-metric-header">
            <div className="profile-metric-icon profile-metric-icon--bible">
              <BibleIcon size={22} style={{ color: '#8B5CF6' }} />
            </div>
            <div className="profile-metric-badge">Meditating</div>
          </div>
          <div className="profile-metric-val">
            <span>{formatTimeSpent(profile.totalTimeSpent)}</span>
          </div>
          <div className="profile-metric-title">Time in the Word</div>
          <div className="profile-metric-sub">
            Dedicated exploring Scripture questions
          </div>
        </div>

        {/* Card 3: Accuracy Rate */}
        <div className="profile-metric-card">
          <div className="profile-metric-header">
            <div className="profile-metric-icon profile-metric-icon--target">
              <span>🎯</span>
            </div>
            <div className="profile-metric-badge">Precision</div>
          </div>
          <div className="profile-metric-val">
            <span>{profile.averageAccuracy}%</span>
          </div>
          <div className="profile-metric-title">Accuracy Rate</div>
          <div className="profile-metric-sub">
            Average accuracy on submitted answers
          </div>
        </div>

        {/* Card 4: Questions Attempted */}
        <div className="profile-metric-card">
          <div className="profile-metric-header">
            <div className="profile-metric-icon profile-metric-icon--questions">
              <span>❓</span>
            </div>
            <div className="profile-metric-badge">Trivia</div>
          </div>
          <div className="profile-metric-val">
            <span>{profile.totalQuestionsAttempted}</span>
          </div>
          <div className="profile-metric-title">Questions Attempted</div>
          <div className="profile-metric-sub">
            Scripture queries tackled across all games
          </div>
        </div>
      </div>

      {/* Spread the Word / Referral Hub (Only on Own Profile) */}
      {isOwnProfile && (
        <div className="profile-referral-card">
          <div className="profile-referral-top">
            <div className="profile-referral-title-wrap">
              <div className="profile-referral-icon">🎁</div>
              <div>
                <h3 className="profile-referral-title">Invite Friends & Spread the Word</h3>
                <p className="profile-referral-desc">
                  Share your referral link with church family and friends to grow our Bible community.
                </p>
              </div>
            </div>

            <div className="profile-referral-stats">
              <div className="profile-ref-stat-item">
                <div className="profile-ref-stat-num">{profile.referralStats?.invitesCount || 0}</div>
                <div className="profile-ref-stat-txt">Invites Sent</div>
              </div>
              <div className="profile-ref-stat-item">
                <div className="profile-ref-stat-num">{profile.referralStats?.signupsCount || 0}</div>
                <div className="profile-ref-stat-txt">Joined</div>
              </div>
              <div className="profile-ref-stat-item">
                <div className="profile-ref-stat-num">{profile.referralStats?.activePlayersCount || 0}</div>
                <div className="profile-ref-stat-txt">Active Players</div>
              </div>
            </div>
          </div>

          <div className="profile-referral-actions-grid">
            {/* Referral Code Box */}
            <div className="profile-code-box">
              <div className="profile-code-meta">
                <span className="profile-code-label">YOUR REFERRAL CODE</span>
                <span className="profile-code-val">{referralCode || 'BCPLAY'}</span>
              </div>
              <button
                type="button"
                className="profile-copy-btn"
                onClick={handleCopyCode}
              >
                {copiedCode ? '✓ Copied!' : 'Copy Code'}
              </button>
            </div>

            {/* Referral Link Box */}
            <div className="profile-code-box">
              <div className="profile-code-meta" style={{ overflow: 'hidden' }}>
                <span className="profile-code-label">YOUR INVITATION LINK</span>
                <span className="profile-link-val">{referralLink || 'go.biblecontest.app/join'}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="profile-copy-btn"
                  onClick={handleCopyLink}
                >
                  {copiedLink ? '✓ Copied!' : 'Copy Link'}
                </button>
                <button
                  type="button"
                  className="profile-share-btn"
                  onClick={handleShareLink}
                  title="Share invitation"
                >
                  📤 Share
                </button>
              </div>
            </div>
          </div>

          <div className="profile-scripture-quote">
            <span>“Go into all the world and proclaim the gospel to the whole creation.”</span>
            <span style={{ fontWeight: 600 }}>— Mark 16:15</span>
          </div>
        </div>
      )}

      {/* Recent Activity Section */}
      <div className="profile-section-card">
        <div className="profile-section-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>📜</span>
            <h3 className="profile-section-title">Recent Contests Played</h3>
          </div>
          <button
            type="button"
            className="profile-view-all-btn"
            onClick={() => navigate(ROUTES.GAMES.PLAY)}
          >
            Play Games →
          </button>
        </div>

        {recentGames.length === 0 ? (
          <div className="profile-empty-state">
            <div className="profile-empty-icon">📖</div>
            <div className="profile-empty-title">No Recent Games Recorded</div>
            <p className="profile-empty-sub">
              {isOwnProfile
                ? 'Join an active season to answer Bible trivia questions, climb the rankings, and earn contest points!'
                : 'This contestant has not completed any public games recently.'}
            </p>
            {isOwnProfile && (
              <button
                type="button"
                className="profile-primary-btn"
                onClick={() => navigate(ROUTES.GAMES.PLAY)}
                style={{ marginTop: '12px' }}
              >
                Find an Active Quiz
              </button>
            )}
          </div>
        ) : (
          <div className="profile-recent-list">
            {recentGames.map((game) => (
              <div key={game.id} className="profile-recent-item">
                <div className="profile-recent-left">
                  <div className="profile-recent-quiz-icon">
                    <GameControllerIcon size={16} />
                  </div>
                  <div>
                    <div className="profile-recent-quiz-title">{game.title}</div>
                    <div className="profile-recent-quiz-time">
                      {formatDate(game.timestamp) || 'Recently played'}
                    </div>
                  </div>
                </div>

                <div className="profile-recent-right">
                  <div className="profile-recent-score">
                    +{game.score} <span style={{ fontSize: '11px', color: 'var(--clr-text-muted)' }}>pts</span>
                  </div>
                  {game.timeTaken > 0 && (
                    <div className="profile-recent-duration">
                      ⏱ {formatTimeSpent(game.timeTaken)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Account Settings & Safety Options (Only on Own Profile) */}
      {isOwnProfile && (
        <div className="profile-account-settings-card">
          <h4 className="profile-settings-title">Account Safety & Legal Controls</h4>
          <div className="profile-settings-links">
            <Link to={ROUTES.PUBLIC.SUPPORT} className="profile-setting-link-row">
              <div className="profile-setting-link-left">
                <span className="profile-setting-icon">💬</span>
                <div>
                  <div className="profile-setting-name">Help & Support Center</div>
                  <div className="profile-setting-desc">Have questions or need assistance with your score?</div>
                </div>
              </div>
              <span className="profile-setting-arrow">→</span>
            </Link>

            <Link to={ROUTES.LEGAL.DELETION_REQUEST} className="profile-setting-link-row">
              <div className="profile-setting-link-left">
                <span className="profile-setting-icon">🛡️</span>
                <div>
                  <div className="profile-setting-name">Account & Personal Data Erasure Portal</div>
                  <div className="profile-setting-desc">
                    Submit a formal deletion request under Google Play and Apple App Store data policies.
                  </div>
                </div>
              </div>
              <span className="profile-setting-arrow">→</span>
            </Link>
          </div>

          <div className="profile-signout-row">
            <button
              type="button"
              className="profile-signout-btn"
              onClick={async () => {
                await logout();
                navigate(ROUTES.PUBLIC.HOME);
              }}
            >
              <span>🚪</span>
              <span>Sign Out of Account</span>
            </button>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {isOwnProfile && (
        <EditProfileModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          profile={profile}
          onProfileUpdated={(updated) => setProfile(updated)}
        />
      )}
    </div>
  );
};

export default UserProfilePage;
